import React, { useMemo, useState } from "react";
import { Button, Grid, MenuItem, Stack, TextField, Typography } from "@mui/material";

import { PagedTable, SectionCard, StatusChip, formatDateTime, safeArray, toTitleCase } from "../coreNetworkUiShared";

const emptyAssignForm = {
  badgeId: "",
  expiresAt: "",
};

const emptyBadgeForm = {
  code: "",
  label: "",
  badgeType: "trust",
  description: "",
  isPaid: "false",
  isActive: "true",
};

export default function CoreNetworkBadgePanel({
  badges = [],
  memberBadges = [],
  membershipId = null,
  saving = false,
  canManage = false,
  onAssignBadge,
  onRevokeBadge,
  onCreateBadge,
  onUpdateBadge,
}) {
  const [assignForm, setAssignForm] = useState(emptyAssignForm);
  const [badgeForm, setBadgeForm] = useState(emptyBadgeForm);

  const activeAssignments = useMemo(
    () => [...safeArray(memberBadges)].sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0)),
    [memberBadges]
  );

  return (
    <SectionCard title="Badges" subtitle="Verified, vetted, premium, and featured trust markers. Sponsored placement is disclosed separately from organic ranking.">
      <Stack spacing={3}>
        <PagedTable
          rows={activeAssignments.length ? activeAssignments : safeArray(badges)}
          columns={[
            {
              key: "badge",
              label: "Badge",
              render: (row) => (
                <Stack spacing={0.5}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {row.badge?.label || row.label || row.badge?.code || row.code || row.badgeId}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {toTitleCase(row.badge?.badgeType || row.badgeType || "badge")}
                  </Typography>
                </Stack>
              ),
            },
            { key: "status", label: "Status", render: (row) => <StatusChip value={row.status || (row.isActive ? "active" : "inactive")} /> },
            { key: "scope", label: "Scope", render: (row) => row.instance?.instanceName || "Global" },
            { key: "expiresAt", label: "Expires", render: (row) => formatDateTime(row.expiresAt) },
            {
              key: "actions",
              label: "",
              render: (row) =>
                canManage ? (
                  row.badge ? (
                    String(row.status || "").toLowerCase() === "active" ? (
                      <Button size="small" color="error" onClick={() => onRevokeBadge?.(row)}>
                        Revoke
                      </Button>
                    ) : null
                  ) : (
                    <Button size="small" onClick={() => onUpdateBadge?.(row.id, { isActive: !row.isActive })}>
                      {row.isActive ? "Deactivate" : "Activate"}
                    </Button>
                  )
                ) : null,
            },
          ]}
          emptyTitle={activeAssignments.length ? "No badges assigned" : "No badge definitions yet"}
          emptyDescription={activeAssignments.length ? "Badge assignments will appear here for this provider." : "Create badge definitions here for later assignment to providers."}
        />

        {canManage && onAssignBadge ? (
          <Stack spacing={2}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
              Assign badge
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  select
                  fullWidth
                  label="Badge"
                  value={assignForm.badgeId}
                  onChange={(event) => setAssignForm((prev) => ({ ...prev, badgeId: event.target.value }))}
                >
                  {safeArray(badges).map((badge) => (
                    <MenuItem key={badge.id} value={String(badge.id)}>
                      {badge.label} ({badge.code})
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  type="datetime-local"
                  label="Expires At"
                  InputLabelProps={{ shrink: true }}
                  value={assignForm.expiresAt}
                  onChange={(event) => setAssignForm((prev) => ({ ...prev, expiresAt: event.target.value }))}
                />
              </Grid>
            </Grid>
            <div>
              <Button
                variant="outlined"
                disabled={saving || !assignForm.badgeId}
                onClick={async () => {
                  await onAssignBadge({
                    badgeId: Number(assignForm.badgeId),
                    membershipId,
                    expiresAt: assignForm.expiresAt || null,
                  });
                  setAssignForm(emptyAssignForm);
                }}
              >
                {saving ? "Saving..." : "Assign badge"}
              </Button>
            </div>
          </Stack>
        ) : null}

        {canManage && onCreateBadge ? (
          <Stack spacing={2}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
              Create badge definition
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={3}>
                <TextField fullWidth label="Code" value={badgeForm.code} onChange={(event) => setBadgeForm((prev) => ({ ...prev, code: event.target.value }))} />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField fullWidth label="Label" value={badgeForm.label} onChange={(event) => setBadgeForm((prev) => ({ ...prev, label: event.target.value }))} />
              </Grid>
              <Grid item xs={12} md={2}>
                <TextField fullWidth label="Type" value={badgeForm.badgeType} onChange={(event) => setBadgeForm((prev) => ({ ...prev, badgeType: event.target.value }))} />
              </Grid>
              <Grid item xs={12} md={2}>
                <TextField select fullWidth label="Paid" value={badgeForm.isPaid} onChange={(event) => setBadgeForm((prev) => ({ ...prev, isPaid: event.target.value }))}>
                  <MenuItem value="false">No</MenuItem>
                  <MenuItem value="true">Yes</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12} md={2}>
                <TextField select fullWidth label="Active" value={badgeForm.isActive} onChange={(event) => setBadgeForm((prev) => ({ ...prev, isActive: event.target.value }))}>
                  <MenuItem value="true">Active</MenuItem>
                  <MenuItem value="false">Inactive</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth label="Description" value={badgeForm.description} onChange={(event) => setBadgeForm((prev) => ({ ...prev, description: event.target.value }))} />
              </Grid>
            </Grid>
            <div>
              <Button
                variant="outlined"
                disabled={saving || !badgeForm.code || !badgeForm.label}
                onClick={async () => {
                  await onCreateBadge({
                    ...badgeForm,
                    isPaid: badgeForm.isPaid === "true",
                    isActive: badgeForm.isActive === "true",
                  });
                  setBadgeForm(emptyBadgeForm);
                }}
              >
                {saving ? "Saving..." : "Create badge"}
              </Button>
            </div>
          </Stack>
        ) : null}
      </Stack>
    </SectionCard>
  );
}
